import { Link } from "react-router-dom";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { NavMenuLink } from "@/components/marketing/NavMenuLink";
import type { NavItem } from "@/lib/navDefaults";
import { joinStoryHint } from "@/components/why-join/copy";
import clipStudioMobile from "@/assets/marketing/clip-studio-mobile.webp.asset.json";
import { useAuth } from "@/contexts/AuthContext";

/** Wide dropdown body for one mega menu heading: its links plus the Clip Studio banner. */
export function MegaMenuPanel({
  column,
  onAction,
  onInternalNavigate,
}: {
  column: NavItem;
  onAction: (key: string) => void;
  onInternalNavigate: (href: string) => void;
}) {
  const { user } = useAuth();
  return (
    <div className="grid gap-4 p-2 sm:grid-cols-[1fr_15rem]">
      <div className="flex flex-col">
        <p className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{column.label}</p>
        {column.children.map((child) => {
          const hint = joinStoryHint(child.href);
          return (
            <DropdownMenuItem key={child.id} asChild>
              <NavMenuLink
                item={child}
                className="flex min-h-11 w-full cursor-pointer flex-col !items-start justify-center text-left text-base"
                onAction={onAction}
                onInternalNavigate={onInternalNavigate}
              >
                <span>{child.label}</span>
                {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
              </NavMenuLink>
            </DropdownMenuItem>
          );
        })}
      </div>
      <Link
        // Signed-out visitors can't open Clip Studio, so send them to its homepage section.
        to={user ? "/clip-studio" : "/#clip-studio"}
        onClick={(e) => {
          if (user || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
          e.preventDefault();
          onInternalNavigate("/#clip-studio");
        }}
        className="group flex overflow-hidden rounded-lg bg-primary text-primary-foreground"
      >
        <div className="flex flex-1 flex-col justify-between p-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-accent-gold">New</p>
            <p className="mt-1 font-semibold leading-snug">Clip Studio</p>
            <p className="mt-1 text-xs opacity-80">Turn episodes into short social clips.</p>
          </div>
          <span className="mt-3 text-sm font-semibold text-accent-gold group-hover:underline">Try it →</span>
        </div>
        <img
          src={clipStudioMobile.url}
          alt="Clip Studio on a phone"
          loading="lazy"
          className="w-24 object-cover object-top"
        />
      </Link>
    </div>
  );
}
